// Web Audio API sound effects â€” no audio files needed
let audioCtx = null;
let masterGain = null;

const VOLUME_KEY = "erewhondle-volume";

function getContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = audioCtx.createGain();
    masterGain.connect(audioCtx.destination);
    // Restore saved volume
    const saved = localStorage.getItem(VOLUME_KEY);
    masterGain.gain.value = saved !== null ? parseFloat(saved) : 0.7;
  }
  // Resume if suspended (browsers require user gesture)
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

function getMasterGain() {
  getContext(); // ensure init
  return masterGain;
}

export function getVolume() {
  const saved = localStorage.getItem(VOLUME_KEY);
  return saved !== null ? parseFloat(saved) : 0.7;
}

export function setVolume(v) {
  const clamped = Math.max(0, Math.min(1, v));
  localStorage.setItem(VOLUME_KEY, String(clamped));
  if (masterGain) {
    masterGain.gain.value = clamped;
  }
}

// Soft pop when submitting a guess
export function playGuess() {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(getMasterGain());
  osc.type = "sine";
  osc.frequency.setValueAtTime(600, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.06);
  gain.gain.setValueAtTime(0.1, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.12);
}

// Satisfying ascending chime on correct guess
export function playCorrect() {
  const ctx = getContext();
  const now = ctx.currentTime;
  const notes = [523, 659, 784]; // C5, E5, G5 â€” major chord arpeggio
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(getMasterGain());
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now + i * 0.1);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + i * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.35);
    osc.start(now + i * 0.1);
    osc.stop(now + i * 0.1 + 0.35);
  });
}

// Subtle dull thud on wrong guess
export function playWrong() {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(getMasterGain());
  osc.type = "triangle";
  osc.frequency.setValueAtTime(250, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.15);
  gain.gain.setValueAtTime(0.12, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.2);
}

// Descending sad tone on loss
export function playLoss() {
  const ctx = getContext();
  const now = ctx.currentTime;
  const notes = [392, 330, 262]; // G4, E4, C4 â€” descending
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(getMasterGain());
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now + i * 0.15);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + i * 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.3);
    osc.start(now + i * 0.15);
    osc.stop(now + i * 0.15 + 0.3);
  });
}

// Celebratory fanfare on perfect day (5/5)
export function playPerfectDay() {
  const ctx = getContext();
  const now = ctx.currentTime;
  // C major scale run + octave: C5 D5 E5 F5 G5 A5 B5 C6
  const notes = [523, 587, 659, 698, 784, 880, 988, 1047];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(getMasterGain());
    osc.type = "sine";
    const t = now + i * 0.07;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.start(t);
    osc.stop(t + 0.4);
  });
}

// First-try correct: triumphant horn-like fanfare
export function playFirstTry() {
  const ctx = getContext();
  const now = ctx.currentTime;
  // Trumpet-like: C5 â†’ E5 â†’ G5 (held) with brass timbre
  const notes = [
    { freq: 523, start: 0, dur: 0.15 },
    { freq: 659, start: 0.12, dur: 0.15 },
    { freq: 784, start: 0.24, dur: 0.5 },
  ];
  notes.forEach(({ freq, start, dur }) => {
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(getMasterGain());
    osc.type = "sawtooth";
    osc2.type = "square";
    osc.frequency.setValueAtTime(freq, now + start);
    osc2.frequency.setValueAtTime(freq * 1.002, now + start); // slight detune for brass feel
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.08, now + start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
    osc.start(now + start);
    osc.stop(now + start + dur);
    osc2.start(now + start);
    osc2.stop(now + start + dur);
  });
}

// Encouraging sound for 3-4 correct
export function playGoodJob() {
  const ctx = getContext();
  const now = ctx.currentTime;
  // Two ascending notes: G4 â†’ C5
  const notes = [392, 523];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(getMasterGain());
    osc.type = "sine";
    const t = now + i * 0.15;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.1, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.start(t);
    osc.stop(t + 0.3);
  });
}

// Womp-womp for all wrong
export function playBoo() {
  const ctx = getContext();
  const now = ctx.currentTime;
  // Classic "wah wah" descending: Bb3 â†’ A3 (slow slide down)
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(getMasterGain());
  osc.type = "triangle";
  osc.frequency.setValueAtTime(233, now); // Bb3
  osc.frequency.linearRampToValueAtTime(220, now + 0.3); // A3
  osc.frequency.linearRampToValueAtTime(185, now + 0.6); // slide down
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
  osc.start(now);
  osc.stop(now + 0.8);
  // Second wah
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2);
  gain2.connect(getMasterGain());
  osc2.type = "triangle";
  osc2.frequency.setValueAtTime(185, now + 0.5);
  osc2.frequency.linearRampToValueAtTime(155, now + 1.0);
  gain2.gain.setValueAtTime(0.1, now + 0.5);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
  osc2.start(now + 0.5);
  osc2.stop(now + 1.2);
}

// Sound preference helpers
const STORAGE_KEY = "erewhondle-soundEnabled";

// Close call "phew" â€” wobbly relieved exhale
export function playPhew() {
  const ctx = getContext();
  const now = ctx.currentTime;
  // Descending wobble: starts tense, resolves with relief
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(getMasterGain());
  osc.type = "sine";
  osc.frequency.setValueAtTime(500, now);
  osc.frequency.linearRampToValueAtTime(420, now + 0.15);
  osc.frequency.linearRampToValueAtTime(460, now + 0.25);
  osc.frequency.linearRampToValueAtTime(350, now + 0.5);
  gain.gain.setValueAtTime(0.1, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
  osc.start(now);
  osc.stop(now + 0.6);
  // Resolution tone
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2);
  gain2.connect(getMasterGain());
  osc2.type = "sine";
  osc2.frequency.setValueAtTime(392, now + 0.3); // G4
  gain2.gain.setValueAtTime(0.08, now + 0.3);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
  osc2.start(now + 0.3);
  osc2.stop(now + 0.7);
}

export function isSoundEnabled() {
  return localStorage.getItem(STORAGE_KEY) !== "false"; // default ON
}

export function setSoundEnabled(enabled) {
  localStorage.setItem(STORAGE_KEY, String(enabled));
}
